import { useTranslation } from 'react-i18next'
import { ArrowLeft, Lightbulb, AlertTriangle, AlertCircle } from 'lucide-react'
import type { HelpCategory, HelpSection, HelpTip } from './helpContent'

interface HelpArticleProps {
  category: HelpCategory
  onBack: () => void
}

/**
 * Displays a full help category with all its sections,
 * numbered steps, and contextual tips.
 */
export function HelpArticle({ category, onBack }: HelpArticleProps) {
  const { t } = useTranslation()
  const Icon = category.icon

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 px-3 py-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:underline mb-1"
        >
          <ArrowLeft size={12} />
          {t('help.backToHelp')}
        </button>
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-primary-500 dark:text-primary-400 shrink-0" />
          <h1 className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
            {t(category.titleKey)}
          </h1>
        </div>
      </div>

      {/* Sections */}
      <div className="px-3 py-3 space-y-5">
        {category.sections.map((section) => (
          <SectionBlock key={section.id} section={section} />
        ))}
      </div>
    </div>
  )
}

// ============================================================
// Section Block
// ============================================================

function SectionBlock({ section }: { section: HelpSection }) {
  const { t } = useTranslation()

  return (
    <div>
      {/* Section title */}
      <h2 className="text-[13px] font-semibold text-neutral-800 dark:text-neutral-200 mb-1.5">
        {t(section.titleKey)}
      </h2>

      {/* Intro */}
      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 leading-relaxed mb-3">
        {t(section.introKey)}
      </p>

      {/* Steps */}
      <div className="space-y-2.5">
        {section.steps.map((step, i) => (
          <div key={i} className="flex gap-2.5">
            {/* Step number */}
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
              <span className="text-[10px] font-bold text-primary-700 dark:text-primary-300">
                {i + 1}
              </span>
            </div>
            {/* Step content */}
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-medium text-neutral-800 dark:text-neutral-200 leading-snug">
                {t(step.titleKey)}
              </p>
              <p className="text-[10px] text-neutral-500 dark:text-neutral-400 leading-relaxed mt-0.5">
                {t(step.descriptionKey)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Tips */}
      {section.tips && section.tips.length > 0 && (
        <div className="mt-3 space-y-2">
          {section.tips.map((tip, i) => (
            <TipBox key={i} tip={tip} />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================
// Tip Box
// ============================================================

const tipStyles = {
  tip: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    icon: Lightbulb,
    iconColor: 'text-blue-500 dark:text-blue-400',
    text: 'text-blue-700 dark:text-blue-300',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    icon: AlertTriangle,
    iconColor: 'text-amber-500 dark:text-amber-400',
    text: 'text-amber-700 dark:text-amber-300',
  },
  important: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    icon: AlertCircle,
    iconColor: 'text-red-500 dark:text-red-400',
    text: 'text-red-700 dark:text-red-300',
  },
} as const

function TipBox({ tip }: { tip: HelpTip }) {
  const { t } = useTranslation()
  const style = tipStyles[tip.variant]
  const Icon = style.icon

  return (
    <div className={`flex gap-2 p-2 rounded-md border ${style.bg} ${style.border}`}>
      <Icon size={12} className={`${style.iconColor} shrink-0 mt-0.5`} />
      <p className={`text-[10px] leading-relaxed ${style.text}`}>
        {t(tip.textKey)}
      </p>
    </div>
  )
}
